import { PaginationConfig } from 'src/components/common/PaginatedGentlyTable';
import {
  AS400_EXPORT_WORKSHEET_COLUMNS,
  as400ColumnEditKey,
  type As400ExportGroup,
  type ListAs400ExportResponse,
} from '@erp/rpc';
import type { FilterColumnOption } from './AdvancedFilters';

/**
 * A worksheet's filterable columns — the review grid's advanced filters and
 * Review mode's Column filter — under exactly the titles the grid shows them
 * under.
 *
 * A worksheet's headings differ from the identity its cells are read by
 * ("PRODUCT NUMBER" → `ITEMNO`), so those carry both. Export-only columns are
 * offered too — they hold staged values, so there is something to filter on.
 * A column with neither a field code nor a key (the Item Add spacer) is always
 * blank and read-only, so it is left out.
 */
export function worksheetFilterColumns(
  group: As400ExportGroup,
): FilterColumnOption[] {
  const seen = new Set<string>();
  return AS400_EXPORT_WORKSHEET_COLUMNS[group].flatMap((column) => {
    const editKey = as400ColumnEditKey(column);
    if (!editKey || seen.has(editKey)) return [];
    seen.add(editKey);
    return [
      {
        value: editKey,
        label: column.header || editKey,
        hint: column.header ? editKey : null,
      },
    ];
  });
}

// The pager's row-window state for one export page. Change-set scope pages
// over a local membership table, so it has exact totals; the unscoped view is
// cursor-style (`totalItems` omitted hides page numbers automatically).
function buildAs400PageState(
  page: number,
  pageSize: number,
  pageInfo: ListAs400ExportResponse['pageInfo'] | undefined,
): PaginationConfig['pagination'] {
  const totalItems = pageInfo?.totalCount;
  return {
    currentPage: page,
    pageSize,
    hasNextPage: pageInfo?.hasNextPage ?? false,
    hasPreviousPage: pageInfo?.hasPreviousPage ?? page > 1,
    ...(totalItems !== undefined && {
      totalItems,
      startItem: totalItems > 0 ? (page - 1) * pageSize + 1 : 0,
      endItem: Math.min(page * pageSize, totalItems),
    }),
  };
}

/**
 * The export table's pagination config: cursor-style prev/next always work;
 * page numbers and last-page appear only when the (change-set-scoped) view
 * carries exact totals.
 *
 * `onNextPage` overrides the default "advance the page number" step — the
 * unscoped view advances by the cursor the response handed back instead, since
 * it has no addressable page numbers.
 */
export function buildAs400PaginationConfig({
  page,
  pageSize,
  pageInfo,
  onPageChange,
  onPageSizeChange,
  onNextPage,
}: {
  page: number;
  pageSize: number;
  pageInfo: ListAs400ExportResponse['pageInfo'] | undefined;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onNextPage?: () => void;
}): PaginationConfig {
  const pagination = buildAs400PageState(page, pageSize, pageInfo);
  const totalPages = pageInfo?.totalPages;
  return {
    enabled: true,
    pagination,
    onPageSizeChange,
    onPreviousPage: () => onPageChange(Math.max(1, page - 1)),
    onNextPage:
      onNextPage ??
      (() => onPageChange(pagination.hasNextPage ? page + 1 : page)),
    onFirstPage: () => onPageChange(1),
    ...(totalPages !== undefined && {
      onLastPage: () => onPageChange(totalPages || 1),
    }),
    showRowsPerPage: true,
    showPageInfo: true,
    showPageNumbers: pagination.totalItems !== undefined,
  };
}

/** Normalize a grid/ingest cell value to the API's text-or-null shape. */
export function asCellText(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value);
  return text === '' ? null : text;
}

/**
 * The value a change-set export cell persists as: the staged edit when the
 * change set has one for the field, else the live ingest value. Normalized
 * through {@link asCellText} so a blank ingest field (empty string) and an
 * unset one (null) compare equal — otherwise an untouched empty cell would read
 * as an unsaved edit against the grid's asCellText-normalized current value.
 */
export function persistedCellValue(
  row: {
    values: Record<string, string | null>;
    stagedValues?: Record<string, unknown> | null;
  },
  code: string,
): string | null {
  if (row.stagedValues && code in row.stagedValues) {
    return asCellText(row.stagedValues[code]);
  }
  return asCellText(row.values[code]);
}

/**
 * The value that returns a cell to what the AS/400 has, discarding unsaved and
 * saved edits alike: what the grid's reset writes. For a field the change set
 * stages, that is its drift baseline (the live value when the field was first
 * edited). The server reverts a field saved back to its baseline, so this
 * still drops the staged edit when the live value has drifted since. Any
 * other field goes back to its live value. A row with no ingest source (an
 * authored new row) has no live value, so its cells go back to what is saved.
 */
export function revertCellValue(
  row: {
    sources: readonly unknown[];
    values: Record<string, string | null>;
    stagedValues?: Record<string, unknown> | null;
    stagedOriginalValues?: Record<string, string | null> | null;
  },
  code: string,
): string | null {
  if (row.sources.length === 0) return persistedCellValue(row, code);
  const baselines = row.stagedOriginalValues;
  if (baselines && code in baselines) return asCellText(baselines[code]);
  return asCellText(row.values[code]);
}
